/** The ten-hertz clock the readouts run on.
 *
 * Frames arrive four times a second; a lap timer that only moved four times a
 * second would look broken, because a hundredth-second digit that stands
 * still for 250 ms reads as a frozen page rather than a slow one. So the
 * numbers are re-derived ten times a second from the buffer — the same
 * interpolation the car is drawn with, sampled at a rate a person can follow
 * — and the lap clock carries on between frames because elapsed time is the
 * one quantity that certainly kept running.
 *
 * Ten hertz rather than sixty: this is the rate the design asks for, it is
 * fast enough that the last digit is legibly in motion, and it keeps the
 * re-render to a handful of small numbers. The car itself is not on this
 * clock — `LiveMap` moves it with `requestAnimationFrame` and no React state
 * at all, because a transform on one element every sixteen milliseconds is
 * cheap and a component tree every sixteen milliseconds is not.
 */

import { useEffect, useState } from 'react';

import { DELAY_MS, interpolateAt, renderClock } from './interpolate';
import type { CarState, Sample } from './interpolate';

/** The design's figure: the current lap ticks at 10 Hz between frames. */
export const TICK_MS = 100;

/** A read-only view of the buffer the socket hook fills. */
export interface SampleRef {
  readonly current: Sample[];
}

/**
 * The car as the readouts should show it, re-derived every `tickMs`.
 *
 * `active` stops the timer when there is nothing to show — an ended or
 * private stream keeps no clock running behind the message that says so.
 */
export function useCarState(
  samples: SampleRef,
  active: boolean,
  tickMs: number = TICK_MS,
  delayMs: number = DELAY_MS,
): CarState | null {
  const [state, setState] = useState<CarState | null>(null);

  useEffect(() => {
    if (!active) return;

    const read = () => setState(interpolateAt(samples.current, renderClock(Date.now(), delayMs)));
    read();
    const timer = setInterval(read, tickMs);
    return () => clearInterval(timer);
  }, [samples, active, tickMs, delayMs]);

  return active ? state : null;
}
